/**
 * useClientOfferReviewViewState
 *
 * Owns search, filter values (tab + FilterPanel selections), sort key,
 * photo mode, page, page size, layout step, expanded/active/pulse ids, and
 * lightbox modal state for the redesigned client-offer-review shell. No
 * detail state — "Zobacz szczegóły" navigates instead of opening a modal
 * (see ClientOfferReviewShell's `handleOpenDetail`). Decision and period
 * state is NOT reimplemented here — the caller
 * composes the existing `useClientOfferSelections` and passes `decisions`
 * in; changing page/page size/sort/photo mode never touches it.
 *
 * The mockup-only `apps/mockups/.../use-offer-review-state.ts` is the
 * behavioral reference (it additionally owns mock-only offer-state cycling
 * and decision/accept/reject state that has no place in real data) — it is
 * not imported here.
 */
import { useMemo, useState } from 'react';
import type {
  ClientOfferReviewDecision,
  ClientOfferReviewFilterKey,
  ClientOfferReviewLayoutStep,
  ClientOfferReviewPageSize,
  ClientOfferReviewPhotoMode,
  ClientOfferReviewSortKey,
  ClientOfferReviewUnit,
} from '../../client-offer-review';
import { compareReviewRows, matchesReviewRowFilter } from '../lib/client-offer-review-row-filter';

export interface UseClientOfferReviewViewStateParams {
  units: ClientOfferReviewUnit[];
  decisions: Record<string, ClientOfferReviewDecision>;
}

interface RowFilterState {
  query: string;
  filter: ClientOfferReviewFilterKey;
  panelFilters: Record<string, string[]>;
  sortKey: ClientOfferReviewSortKey;
}

/** Full filtered + sorted row list — split out to keep the hook's own body under the complexity budget. */
function useFilteredRows(
  units: ClientOfferReviewUnit[],
  decisions: Record<string, ClientOfferReviewDecision>,
  { query, filter, panelFilters, sortKey }: RowFilterState,
): ClientOfferReviewUnit[] {
  return useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return units
      .filter((unit) =>
        matchesReviewRowFilter(unit, decisions, filter, normalizedQuery, panelFilters),
      )
      .sort((a, b) => compareReviewRows(a, b, sortKey, decisions));
  }, [units, decisions, query, filter, panelFilters, sortKey]);
}

// eslint-disable-next-line max-lines-per-function -- single cohesive view-state hub, mirrors the mockup reference's shape
export function useClientOfferReviewViewState({
  units,
  decisions,
}: UseClientOfferReviewViewStateParams) {
  const [query, setQuery] = useState('');
  const [filter, setFilterState] = useState<ClientOfferReviewFilterKey>('ALL');
  const [panelFilters, setPanelFiltersState] = useState<Record<string, string[]>>({});
  const [sortKey, setSortKey] = useState<ClientOfferReviewSortKey>('city');
  const [photoMode, setPhotoMode] = useState<ClientOfferReviewPhotoMode>('large');
  const [layoutStep, setLayoutStep] = useState<ClientOfferReviewLayoutStep>('balanced');
  const [pageSize, setPageSizeState] = useState<ClientOfferReviewPageSize>(20);
  const [pageState, setPageState] = useState(1);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pulseId, setPulseId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ id: string; idx: number } | null>(null);

  const rows = useFilteredRows(units, decisions, { query, filter, panelFilters, sortKey });

  const pageCount = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(rows.length / pageSize));
  // Clamp defensively — a decision change can move a row out of `rows` under an active
  // filter and shrink `pageCount` below the page the caller was already on.
  const page = Math.min(pageState, pageCount);
  const startIndex = pageSize === 'all' ? 0 : (page - 1) * pageSize;
  const visibleRows = pageSize === 'all' ? rows : rows.slice(startIndex, startIndex + pageSize);
  const noResults = rows.length === 0;

  function onQuery(value: string) {
    setQuery(value);
    setPageState(1);
  }
  function onFilter(key: ClientOfferReviewFilterKey) {
    setFilterState(key);
    setPageState(1);
  }
  function onPanelFiltersChange(value: Record<string, string[]>) {
    setPanelFiltersState(value);
    setPageState(1);
  }
  function onPageSizeChange(size: ClientOfferReviewPageSize) {
    setPageSizeState(size);
    setPageState(1);
  }
  function onPageChange(next: number) {
    setPageState(Math.min(Math.max(1, next), pageCount));
  }
  function clearFilters() {
    setFilterState('ALL');
    setQuery('');
    setPanelFiltersState({});
  }

  return {
    query,
    onQuery,
    filter,
    onFilter,
    panelFilters,
    onPanelFiltersChange,
    sortKey,
    setSortKey,
    photoMode,
    setPhotoMode,
    layoutStep,
    setLayoutStep,
    pageSize,
    onPageSizeChange,
    page,
    pageCount,
    onPageChange,
    rows,
    visibleRows,
    startIndex,
    noResults,
    clearFilters,
    activeId,
    setActiveId,
    pulseId,
    setPulseId,
    lightbox,
    setLightbox,
  };
}

export type ClientOfferReviewViewState = ReturnType<typeof useClientOfferReviewViewState>;
